import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Typography, useRouteDocumentTitle, Pagination, exportToExcel } from '@client/toolkit';
import { FaCalendarAlt } from 'react-icons/fa';
import { ReportingPaths } from '../reporting.router';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import { OutstandingValueCard } from '../components';
import { OverdueInvoicesTable } from './components/overdue-invoices-table';
import { dateRangeOptions } from '../utils/date-range-options';
import { DateRangeFilter, futureDate } from '../components/date-range-filter';
import {
  useGetInvoicesOverdueSummaryQuery,
  useGetInvoicesOverdueDetailsQuery,
  useLazyGetInvoicesExportOverdueDetailsQuery,
} from '@client/api';
import { transformToExcelFormat } from './utils/transformToExcelFormat';
import dayjs from 'dayjs';

export function OverdueInvoicesRoute() {
  const { t } = useTranslation('reporting');
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const defaultDateRange = {
    startDate: dateRangeOptions().lastTwelveMonths.startDate,
    endDate: dateRangeOptions().lastTwelveMonths.endDate,
  };
  const [timeRange, setTimeRange] = useState<{ startDate: Date; endDate: Date }>({
    startDate: defaultDateRange.startDate,
    endDate: defaultDateRange.endDate,
  });
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);

  useRouteDocumentTitle(t('reporting.documentTitle'), ReportingPaths.Invoices);

  const { data, isFetching } = useGetInvoicesOverdueSummaryQuery();

  const {
    data: detailsData,
    isFetching: isFetchingDetails,
    isLoading: isLoadingDetails,
  } = useGetInvoicesOverdueDetailsQuery(
    {
      startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
      page: page,
      pageSize: 10,
    },
    {
      skip: !timeRange.startDate || !timeRange.endDate,
    },
  );
  const [trigger] = useLazyGetInvoicesExportOverdueDetailsQuery();
  const onExportExcel = useCallback(async () => {
    setExporting(true);
    const result = await trigger({
      startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
      pageSize: 0,
    });
    if (result.isSuccess) {
      exportToExcel(transformToExcelFormat(result.data, selectedRows), 'overdue-invoices-report.xlsx');
    }
    setExporting(false);
  }, [timeRange, trigger, selectedRows]);

  const dateLabel = useMemo(() => {
    return timeRange.startDate.toDateString() === defaultDateRange.startDate.toDateString() &&
      timeRange.endDate.toDateString() === defaultDateRange.endDate.toDateString()
      ? dateRangeOptions().lastTwelveMonths.displayLabel
      : t('dateRange', {
          startDate: timeRange.startDate.toLocaleDateString('no-NO'),
          endDate: timeRange.endDate.toLocaleDateString('no-NO'),
        });
  }, [timeRange]);

  return (
    <ReportPageContent
      breadcrumb={t('navigation.reporting.overdueInvoices')}
      menu={
        <DateRangeFilter
          options={[dateRangeOptions().lastTwelveMonths, dateRangeOptions().custom]}
          onChange={(dateRange) => {
            setTimeRange({ startDate: dateRange[0], endDate: dateRange[1] });
            setPage(0);
            setSelectedRows(new Set());
          }}
          shouldDisableDate={futureDate}
          onExportExcel={onExportExcel}
          exporting={exporting}
        />
      }
    >
      <div className="flex gap-5 justify-between h-[146px] text-white">
        <OutstandingValueCard isLoading={isFetching} title={t('last30Days')} amount={data?.last30DaysAmount} />
        <OutstandingValueCard isLoading={isFetching} title={t('over30Days')} amount={data?.over30DaysAmount} />
        <OutstandingValueCard
          isLoading={isFetching}
          title={t('development')}
          amount={data?.variance.absolute}
          changeValue={data?.variance.percentage}
          prefix={(data?.variance.absolute.value || 0) > 0 ? '+' : ''}
          showChange
          isInverted
        />
      </div>
      {data?.lastSyncTimestamp && (
        <span className="flex justify-end text-brand-primary text-sm">
          {t('lastUpdated', {
            dateTime: new Date(data?.lastSyncTimestamp).toLocaleString('no-NO'),
          })}
        </span>
      )}
      <div>
        <span className="flex items-baseline gap-2 mb-4 text-sm">
          <FaCalendarAlt className="text-white" />
          <Typography className="text-white text-sm">{dateLabel}</Typography>
        </span>
        <Card className="pb-4">
          <OverdueInvoicesTable
            data={detailsData?.items || []}
            loading={isFetchingDetails || isLoadingDetails}
            onSelected={(ids) => {
              setSelectedRows(ids);
            }}
          />
          <Pagination
            totalCount={detailsData?.total ?? 0}
            currentPage={page}
            setPage={setPage}
            pageSize={detailsData?.pageSize ?? 10}
            visible={true}
          />
        </Card>
      </div>
    </ReportPageContent>
  );
}
